/**
 * Customer classification control (item #8).
 *
 * Shows the current tier and opens a picker. Admin/manager set the softer tiers
 * directly; setting or lifting Blacklisted is admin-only, so a manager's option
 * is locked and field staff RAISE a request an admin approves. A data-driven
 * suggestion is shown as a hint.
 */

import { useState } from 'react';
import { Card, Sheet, Button, useToast } from './ui';
import { useQuery } from '../lib/useApi';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

export const TIERS = [
  { value: 'trusted', label: 'Trusted', meaning: 'Credit allowed (within the limit)', color: '#639922' },
  { value: 'cash_only', label: 'Cash only', meaning: 'Spot payment — no credit', color: '#378ADD' },
  { value: 'watch', label: 'Watch', meaning: 'Delayed payer — allowed, with a warning', color: '#EF9F27' },
  { value: 'high_risk', label: 'High risk', meaning: 'Avoid — cash / UPI only', color: '#D85A30' },
  { value: 'blacklisted', label: 'Blacklisted', meaning: 'No sale at all', color: '#E24B4A' },
];
export const TIER_LABEL: Record<string, string> = Object.fromEntries(TIERS.map((t) => [t.value, t.label]));

export function CustomerClassification({ customerId, current, onChanged }: {
  customerId: string; current: string; onChanged: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'owner_admin';
  const isManager = user?.role === 'manager';
  const isFieldStaff = !!user && ['salesperson', 'driver', 'field_agent'].includes(user.role);
  const canSetDirect = isAdmin || isManager;
  const canChange = canSetDirect || isFieldStaff;

  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(current);
  const [busy, setBusy] = useState(false);
  const sugg = useQuery<{ suggestion: string; overdueInvoices: number }>(
    open ? `/api/customers/${customerId}/classification-suggestion` : null,
  );

  // A manager cannot set or lift Blacklisted; field staff can request anything.
  const blacklistLockedForManager = isManager && !isAdmin;
  const lockedEntirely = blacklistLockedForManager && current === 'blacklisted';

  async function save() {
    setBusy(true);
    try {
      if (canSetDirect) {
        await api.post(`/api/customers/${customerId}/classification`, { classification: sel });
        toast.show('Classification updated.', 'ok');
      } else {
        await api.post(`/api/customers/${customerId}/classification-request`, { classification: sel });
        toast.show('Change requested — an admin will review it.', 'ok');
      }
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : 'Could not update', 'danger');
    } finally {
      setBusy(false);
    }
  }

  const tier = TIERS.find((t) => t.value === current);

  return (
    <Card>
      <div className="row-between">
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: tier?.color ?? 'var(--faint)' }} />
          <div>
            <div className="t-xs t-muted">Classification</div>
            <div className="t-semi">{tier?.label ?? current}</div>
          </div>
        </div>
        {canChange && (
          <Button size="sm" variant="ghost" onClick={() => { setSel(current); setOpen(true); }}>
            {isFieldStaff ? 'Request change' : 'Change'}
          </Button>
        )}
      </div>

      {open && (
        <Sheet
          open
          onClose={() => setOpen(false)}
          title="Customer classification"
          footer={(
            <>
              <Button variant="ghost" block onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
              <Button block busy={busy} disabled={lockedEntirely} onClick={() => void save()}>
                {canSetDirect ? 'Save' : 'Request change'}
              </Button>
            </>
          )}
        >
          <div className="stack-2">
            {sugg.data && (
              <div className="t-xs t-warn">
                Suggested: {TIER_LABEL[sugg.data.suggestion] ?? sugg.data.suggestion}
                {' '}· {sugg.data.overdueInvoices} overdue invoice{sugg.data.overdueInvoices === 1 ? '' : 's'}
              </div>
            )}
            {TIERS.map((t) => {
              const blocked = t.value === 'blacklisted' && blacklistLockedForManager;
              const disabled = lockedEntirely || blocked;
              return (
                <label
                  key={t.value}
                  className="row"
                  style={{
                    gap: 10, alignItems: 'flex-start', padding: 10,
                    border: `${sel === t.value ? 1.5 : 0.5}px solid var(${sel === t.value ? '--accent' : '--line'})`,
                    borderRadius: 'var(--radius)', opacity: disabled ? 0.5 : 1,
                    background: sel === t.value ? 'var(--accent-bg)' : undefined,
                  }}
                >
                  <input
                    type="radio" name="tier" value={t.value} checked={sel === t.value} disabled={disabled}
                    onChange={() => setSel(t.value)} style={{ marginTop: 3 }}
                  />
                  <span style={{ width: 11, height: 11, borderRadius: '50%', background: t.color, marginTop: 4 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="t-sm t-semi">{t.label}</span>
                    <span className="t-xs t-muted" style={{ display: 'block' }}>{t.meaning}</span>
                  </span>
                </label>
              );
            })}
            {blacklistLockedForManager && (
              <div className="t-xs t-muted">Only an admin can set or lift Blacklisted.</div>
            )}
          </div>
        </Sheet>
      )}
    </Card>
  );
}
